<template>
  <div class="w-full min-w-0 flex flex-col flex-1 min-h-0">
    <div class="mb-4">
      <nav class="grid grid-cols-2 gap-2 rounded-xl border border-gray-200 bg-gray-100 p-1.5" aria-label="Tabs">
        <template
            v-for="tab in allTabs"
            :key="tab.label"
        >
          <button

              type="button"
              :disabled="tab.isDisabled&& !tab.disabledLabelFallback"
              class="flex w-full items-center justify-center gap-2 rounded-lg px-3  text-sm font-medium leading-5 transition-all duration-200 focus:outline-none focus:ring-2 ring-offset-2 ring-offset-blue-400 ring-white ring-opacity-60"
              :class="[
            tab.isActive
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-gray-500 hover:bg-white/[0.7] hover:text-gray-700',
            tab.isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            !tab.disabledLabelFallback? 'py-2.5': ''
          ]"
              @click="!tab.isDisabled ? activeTab = tab.name : null"
          >
            <div v-if="tab.isLoading && tab.isActive" class="flex items-center">
              <Icon icon="fa6-solid:circle-notch" class="shrink-0 animate-spin text-lg mr-1"/>
              <span>Laden...</span>
            </div>

            <component v-else-if="tab.disabledLabelFallback && tab.isDisabled" :is="FALLBACK_COMPONENTS[tab.disabledLabelFallback]"/>
            <template v-else>
              <Icon v-if="tab.icon" :icon="tab.icon" class="text-lg" :class="tab.isActive ? 'text-blue-600' : 'text-gray-400'"/>
              <span class="truncate">{{ tab.label }}</span>
              <span
                  v-if="tab.badge"
                  class="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-700"
              >{{ tab.badge }}</span>
            </template>

          </button>
        </template>
      </nav>
    </div>

    <!-- `overflow-clip` und kein `overflow-auto` am Slot-Wrapper (kritisch): jeder Scroll-Container hier
         finge die `sticky`-Toolbar von `MusicXmlPreview` ab; gescrollt wird `<main>`, breite Inhalte
         (OSMD) scrollen in ihrem eigenen Container. Guard: sourceGuards.spec.ts. -->
    <div class="flex-1 flex flex-col w-full overflow-clip rounded-xl border border-gray-200 bg-white shadow-sm">
      <div class="animate-fade animate-duration-200 animate-ease-out">
        <!-- :key NUR der Tab-Name (kritisch – isLoading gehört NICHT in den Key): ein Key-Wechsel
             zerstört den Slot-Inhalt samt OSMD-Instanz, PlaybackManager, lokaler Undo-History und
             ungespeicherten Edits – und isLoading flippt bei jedem Analyze/Upload/Transpose.
             Inhalts-Updates übernimmt der file-Watcher in MusicXmlPreview. Guard:
             sourceGuards.spec.ts. -->
        <template v-for="tab in allTabs" :key="tab.name">
          <div v-if="tab.name === activeTab" class="flex flex-1 w-full flex-col p-3 sm:p-4 min-h-0 h-full">
            <div class="min-h-0 min-w-0 flex-1">
              <slot :name="tab.name"/>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, type Component} from "vue";
import {Icon} from '@iconify/vue';
import {useTabStore} from "@/stores/useTabStore.ts";
import type {PreviewTab, PreviewTabDisabledFallback} from "@/components/generic/PreviewTab.ts";
import {storeToRefs} from "pinia";
import ConvertButton from "@/components/ConvertButton.vue";

// Diskriminator → Komponente hier (UI-Schicht), NICHT im Tab-Store: eine Komponente im
// Store-State erzeugte den Modul-Zyklus useTabStore → ConvertButton → Upload-Store → useTabStore.
const FALLBACK_COMPONENTS: Record<PreviewTabDisabledFallback, Component> = {
  'convert-button': ConvertButton,
};

const {activePreviewTab: activeTab, tabs} = storeToRefs(useTabStore());

type PreviewTabView = PreviewTab & { isActive: boolean };

const allTabs = computed<PreviewTabView[]>((oldValue) => {
  const nextTabs = Object.values(tabs.value)
      .filter((tab) => !tab.isHidden)
      .map((tab) => ({
        ...tab,
        isActive: tab.name === activeTab.value,
      }));

  if (
      oldValue &&
      oldValue.length === nextTabs.length &&
      oldValue.every((tab, index) => {
        const nextTab = nextTabs[index];
        if (!nextTab) return false;

        return (
            tab.name === nextTab.name &&
            tab.label === nextTab.label &&
            tab.badge === nextTab.badge &&
            tab.icon === nextTab.icon &&
            tab.isActive === nextTab.isActive &&
            tab.isDisabled === nextTab.isDisabled &&
            tab.isHidden === nextTab.isHidden &&
            tab.isLoading === nextTab.isLoading
        );
      })
  ) {
    return oldValue;
  }

  return nextTabs;
});
</script>
